/**
 * URLs, without a router library.
 *
 * Ported from Fieldpath's `web/src/router.ts`, deliberately: the two apps sit
 * beside each other and a link should behave the same in both. A routing
 * library would add a dependency and a mental model to reimplement what
 * `history.pushState` does in twenty lines.
 *
 * This exists because navigation held in React state cannot be pasted
 * anywhere. "Here is the calendar for November" is a thing people need to be
 * able to send, and holding the screen in a `useState` also breaks refresh and
 * the Back button, which people try without thinking.
 */
import { useEffect, useState } from "react";

export type View = "day" | "week" | "month" | "grid";
export const VIEWS: readonly View[] = ["day", "week", "month", "grid"];

export type Route =
  | { readonly kind: "mine" }
  /** Working from home: the caller's own days, one month at a time. `month` null is this month. */
  | { readonly kind: "wfh"; readonly month: string | null }
  | { readonly kind: "approvals" }
  | { readonly kind: "field" }
  /** HR-only tools. The API refuses everyone else; the tab is only hidden. */
  | { readonly kind: "hr" }
  /**
   * One deployment, at /deployments/<id>: what a copied link points at, and what Fieldpath links
   * to. The id is the deployment's uuid (21 Oct 2026), stable through every edit.
   */
  | { readonly kind: "deployment"; readonly id: string }
  /**
   * /field/<request id>: a link from before 21 Oct, when field work was a request. It is looked up
   * and replaced by the deployment that request became.
   */
  | { readonly kind: "legacyField"; readonly requestId: string }
  /**
   * `month` is `YYYY-MM` (Month and Grid), `date` is `YYYY-MM-DD` (Day and Week), and null is
   * "now" for either. `view` is null when the address names none: the screen then uses the view
   * this browser last chose, or Month (PR 11). An address that carries a month means Month.
   */
  | { readonly kind: "calendar"; readonly month: string | null; readonly date: string | null; readonly view: View | null };

/**
 * Checked against the shape of a month before being treated as one, the same
 * way Fieldpath checks a UUID: without it, "/calendar/favicon.ico" becomes a
 * date range and an error rather than simply not being a calendar route.
 */
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseRoute(pathname: string, search = ""): Route {
  const [first, second] = pathname.replace(/^\/+|\/+$/g, "").split("/");

  // My requests has its own address since 21 Oct 2026, when the site root became the Team
  // calendar (Jeremy). It was "/" until then.
  if (first === "requests") return { kind: "mine" };
  if (first === "approvals") return { kind: "approvals" };
  if (first === "wfh") return { kind: "wfh", month: second && MONTH.test(second) ? second : null };
  if (first === "hr") return { kind: "hr" };
  // Shape-checked before being treated as ids, so /deployments/favicon.ico is simply not a
  // deployment route rather than a lookup and an error.
  if (first === "deployments" && second && UUID.test(second)) return { kind: "deployment", id: second.toLowerCase() };
  if (first === "field" && second && /^\d{1,19}$/.test(second)) {
    return { kind: "legacyField", requestId: second };
  }
  if (first === "field") return { kind: "field" };
  // The site root IS the Team calendar (Jeremy, 21 Oct 2026): where the app opens.
  if (first === "calendar" || first === "") {
    const params = new URLSearchParams(search);
    const asked = params.get("view");
    const view = VIEWS.find((v) => v === asked) ?? null;
    const date = params.get("date");
    return {
      kind: "calendar",
      month: second && MONTH.test(second) ? second : null,
      date: date && DATE.test(date) ? date : null,
      view,
    };
  }

  // Anything unrecognised is the default screen, the Team calendar, rather than a 404 page. A
  // stale or mistyped link should land somewhere useful, not at a dead end.
  return { kind: "calendar", month: null, date: null, view: null };
}

export function pathFor(route: Route): string {
  switch (route.kind) {
    case "approvals":
      return "/approvals";
    case "wfh":
      return route.month ? `/wfh/${route.month}` : "/wfh";
    case "field":
      return "/field";
    case "hr":
      return "/hr";
    case "deployment":
      return `/deployments/${route.id}`;
    case "legacyField":
      return `/field/${route.requestId}`;
    case "calendar": {
      // Day and Week carry a date; Month and Grid a month. Month is left out of the address
      // when it is the view: a URL should carry what somebody chose, not restate the default.
      if (route.view === "day" || route.view === "week") {
        return `/calendar?view=${route.view}${route.date ? `&date=${route.date}` : ""}`;
      }
      const base = route.month ? `/calendar/${route.month}` : "/calendar";
      if (route.view === "grid") return `${base}?view=grid`;
      // An explicit Month on a bare /calendar must still say so, or the remembered view wins.
      return route.view === "month" && !route.month ? `${base}?view=month` : base;
    }
    case "mine":
      return "/requests";
  }
}

/** The full address to hand to someone else. */
export function shareUrl(route: Route): string {
  return `${window.location.origin}${pathFor(route)}`;
}

/**
 * pushState does not fire popstate — that event is only for the user moving
 * through history — so in-app navigation announces itself.
 */
const ROUTE_EVENT = "scheduler:route";

export function navigate(route: Route, replace = false): void {
  const path = pathFor(route);
  if (path !== window.location.pathname + window.location.search) {
    // `replace` for changes that are not really navigation — flipping the
    // calendar's view, say. Otherwise Back walks through every toggle instead
    // of leaving the screen.
    if (replace) window.history.replaceState(null, "", path);
    else window.history.pushState(null, "", path);
  }
  window.dispatchEvent(new Event(ROUTE_EVENT));
}

/** Where the browser currently is, kept in step with Back and Forward. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const sync = (): void => setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener("popstate", sync);
    window.addEventListener(ROUTE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(ROUTE_EVENT, sync);
    };
  }, []);

  return route;
}

/**
 * Carrying the destination across sign-in.
 *
 * Entra returns to the registered redirect URI, which is the site root — so
 * someone opening a calendar link while signed out would sign in successfully
 * and land on the default screen, having lost the thing they clicked. The path
 * is stashed before the redirect and put back afterwards.
 */
const RETURN_KEY = "scheduler:returnTo";

export function rememberReturnPath(): void {
  const path = window.location.pathname + window.location.search;
  if (path === "/") return;
  try {
    window.sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // Private browsing or blocked storage. Sign-in still works; it just lands
    // at the default screen, which is a worse outcome than a crash is.
  }
}

export function restoreReturnPath(): void {
  let saved: string | null = null;
  try {
    saved = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    return;
  }

  // Only when sitting at the root. If the path survived the round trip by some
  // other route, overwriting it would undo a correct answer.
  if (saved && saved !== "/" && window.location.pathname === "/") {
    window.history.replaceState(null, "", saved);
  }
}
